<template>
  <div class="c-main">
    <form action="#" class="c-main--flex-1 u-margin-bottom-small">
      <div class="o-layout">
        <div class="o-layout__item u-1-of-2-at-medium">
          <sceTextField v-model="name" name="name" label="name"
                     v-validate data-vv-rules="required" :class="{'is-invalid': errors.has('name')}" />
          <sceTextField v-model="externalId" name="externalId" label="externalId" />
          <sceTextField v-model="competitionId" name="competitionId" label="category.competitionId" />
        </div>
        <div class="o-layout__item">
          <label class="c-input__label" for="requirements">{{$t('category.requirements')}}</label>
          <tiny-editor v-model="description" id="requirements" name="requirements"></tiny-editor>
        </div>

        <div class="o-layout__item  u-1-of-2-at-medium">
          <div class="o-layout">
            <div class="o-layout__item u-1-of-2-at-small">
              <sceNumberField v-model.number="minAge" name="minAge" label="category.minimalAge"
                         v-validate data-vv-rules="required|numeric|min_value:0|max_value:120"
                         :class="{'is-invalid': errors.has('minAge')}" min="0" max="120" step="1" />
            </div>
            <div class="o-layout__item u-1-of-2-at-small">
              <sceNumberField v-model.number="maxAge" name="maxAge" label="category.maximalAge"
                           v-validate data-vv-rules="required|numeric|min_value:0|max_value:120"
                           :class="{'is-invalid': errors.has('maxAge')}" min="0" max="120" step="1" />
            </div>
            <div class="o-layout__item u-1-of-2-at-small">
              <sceSelect v-model="gender" name="gender" label="gender"
                         :options="$options.genderOptions" v-validate data-vv-rules="required"
                         :class="{'is-invalid': errors.has('gender')}"/>
            </div>
            <div class="o-layout__item u-1-of-2-at-small">
              <sceSelect v-model="participantType" name="participantType" label="participantType"
                         :options="$options.participantTypeOptions"
                         v-validate data-vv-rules="required" :class="{'is-invalid': errors.has('participantType')}"/>
            </div>
            <div class="o-layout__item u-1-of-2-at-small" v-if="participantType === 'group'">
              <sceNumberField v-model.number="groupSize.min" name="groupSizeMin" label="category.minimalGroupSize"
                              v-validate data-vv-rules="numeric|min_value:0|max_value:100"
                              :class="{'is-invalid': errors.has('groupSizeMin')}" min="0" max="100" step="1" />
            </div>
            <div class="o-layout__item u-1-of-2-at-small" v-if="participantType === 'group'">
              <sceNumberField v-model.number="groupSize.max" name="groupSizeMax" label="category.maximalGroupSize"
                              v-validate data-vv-rules="numeric|min_value:0|max_value:100"
                              :class="{'is-invalid': errors.has('groupSizeMax')}" min="0" max="100" step="1" />
            </div>
          </div>
        </div>
      </div>

      <div class="o-layout">
        <div class="o-layout__item u-1-of-2-at-small">
          <sceSelect v-model="scorePublication" name="scorePublication" label="scorePublication"
                     :options="$options.scorePublicationOptions" v-validate data-vv-rules="required" />
        </div>
        <div class="o-layout__item u-1-of-2-at-small">
          <sceSelect v-model="rankingPublication" name="rankingPublication" label="rankingPublication"
                     :options="$options.rankingPublicationOptions" v-validate data-vv-rules="required" />
        </div>
        <div class="o-layout__item u-1-of-2-at-small">
          <sceMultiSelect v-model="includeCategories" :options="getIncludeCategories()" name="includeCategories"
                          label="includeCategory" />
        </div>
        <div class="o-layout__item u-1-of-2-at-small">
          <sceSelect v-model="warmup" :name="'warmup'+1" label="session.warmupConfig" :options="warmupOptions"
                     :config="{optionId: 'name', emptyLabel: 'none', localized: false}" />
        </div>
      </div>

      <div class="c-tabs">
        <div class="c-tab__tab-bar u-margin-bottom-medium">
          <sceButton v-for="catRound of categoryRounds" :key="catRound.round.roundIndex"
                     class="c-button--large c-button--ghost c-tabs__tab-link"
                     :class="{ 'is-active': categoryRound === catRound.round.roundIndex }"
                     v-on:click="categoryRound = catRound.round.roundIndex"
                     :label="$t('round') + ' ' + (catRound.round.roundIndex + 1)"
          />
        </div>
        <template v-for="catRound of categoryRounds">
          <categoryRound v-if="catRound.round.roundIndex === categoryRound" :key="catRound.round.roundIndex"
                    mode="category" class="c-tabs__panel"
                    v-model="catRound.round" :formats="formats" :tie-breaks="tieBreaks" :discipline="discipline" />
        </template>
      </div>
    </form>
    <footer class="c-footer c-footer--bottom-sticky c-footer--bottom-sticky-fixed-height o-layout">
      <div class="o-layout__item u-1-of-2-at-tiny">
        <sceButton v-if="id" class="c-button--secondary c-button--large" v-on:click="remove()" label="delete" />
      </div>

      <div class="o-layout__item u-1-of-2-at-tiny u-text-right">
        <sceRouteButton :route="{ name: 'event.discipline.categories'}" label="cancel"
                        class="c-button--ghost c-button--large" />
        <sceButton class="c-button--primary c-button--large" v-on:click="submit()" label="save" />
      </div>
    </footer>
  </div>
</template>


<script>
  import options from 'client/lib/options'
  import rounds from 'client/lib/rounds'

  import clone from 'lodash/cloneDeep'
  import filter from 'lodash/filter'
  import find from 'lodash/find'
  import map from 'lodash/map'
  import merge from 'lodash/merge'
  import sortBy from 'lodash/sortBy'

  import tinyEditor from "@/components/tinyEditor.vue"

  export default {
    name: "category",
    components: {
      categoryRound: require('client/components/models/categoryRound.vue').default,
      tinyEditor,
    },
    props: ['id'],
    ...options.category,
    data: function () {
      return {
        name: '',
        externalId: undefined,
        competitionId: undefined,
        description: '',
        index: -1,
        minAge: 0,
        maxAge: 120,
        participantType: 'clubMember',
        gender: 'mixed',
        groupSize: {min: undefined, max: undefined},
        scorePublication: 'totals',
        rankingPublication: 'full',
        categoryRounds: [],
        categoryRound: 0,
        includeCategories: [],
        categoryOptions: {},
        warmup: undefined,
      };
    },
    computed: {
      discipline: function() {
        return this.$store.state.eventDiscipline.discipline;
      },
      category: function () {
        return find(this.$store.state.categories.items, (item) => {
          return item.id === this.id;
        });
      },
      categories: function() {
        return sortBy(this.$store.state.categories.items, 'index');
      },
      rounds: function() {
        return sortBy(rounds.getRounds(), 'index');
      },
      eventDiscipline: function() {
        return find(this.$store.state.eventDisciplines.items,
          item => item.id === this.$route.params.disciplineId);
      },
      formats: function() {
        return this.$store.state.eventDiscipline.formats
      },
      tieBreaks: function() {
        return sortBy(this.$store.state.eventDiscipline.tieBreaks, 'index')
      },
      warmupOptions: function() {
        let warmupOptions = []
        const eventDiscipline =
            find(this.$store.state.eventDisciplines.items,  i => i.id === this.$route.params.disciplineId)
        if (eventDiscipline.planningConfig && eventDiscipline.planningConfig.warmupConfigs) {
          warmupOptions = eventDiscipline.planningConfig.warmupConfigs
        }
        return warmupOptions
      },
    },
    watch: {
      category: {
        handler: function () {
          this.setCategory()
        },
        immediate: true,
      },
    },
    methods: {
      setCategory: function () {
        // prepare category rounds with default data
        this.categoryRounds = this.rounds.map(round => {
          const results = {}
          round.rankings.forEach(rt => {
            if (rt.enabled) {
              results[rt.rankingType] = { result: 'reset' }
            }
          })

          return {
            round: {
              formatId: null,
              id: round.index,
              roundIndex: round.index,
              subDivisions: 1,
              name: round.name,
              singleAwardsType: 'score',
              singleAwards: [],
              eventAwardsType: 'score',
              eventAwards: [],
              teamCount: 3,
              teamMethod: 'event',
              teamAwardsType: 'score',
              teamAwards: [],
              tieBreakId: null,
              results,
              selectionCriteria: {
                maxCount: 0,
                reserveCount: 0,
                clubCount: 0,
                minScore: 0,
              },
              exerciseTime: null,
              exerciseTiming: [],
              customFields: [],
              requireAllEvents: false,
              trackSelection: {
                enable: false,
                minima: [],
                requirementsCheck: null,
              },
              config: {},
            }
          }
        })

        // load category data
        if (this.category) {
          this.name = this.category.name
          this.externalId= this.category.accessId
          this.competitionId = this.category.competitionId
          this.description = this.category.description
          this.index = this.category.index
          this.description = this.category.description
          this.minAge = this.category.minAge
          this.maxAge = this.category.maxAge
          this.participantType = this.category.participantType
          this.gender = this.category.gender
          this.groupSize = this.category.groupSize || {min: undefined, max: undefined}
          this.scorePublication = this.category.scorePublication
          this.rankingPublication = this.category.rankingPublication
          let includeCategories = []
          if (this.category.includeCategories) {
            includeCategories = clone(this.category.includeCategories)
          } else if (this.category.includeCategoryId) {
            includeCategories = [this.category.includeCategoryId]
          }
          this.includeCategories = map(includeCategories, cat => find(this.categories, item => item.id === cat))

          let warmup = this.category.warmup
          const warmupOptions = this.warmupOptions
          if (warmup) {
            if (! find(warmupOptions, w => w.name === warmup)) {
              warmup = null
            }
          }
          if (! warmup && warmupOptions.length) {
            warmup = warmupOptions[0].name
          }
          this.warmup = warmup

          // Load category round settings into presets
          this.categoryRounds.forEach(cr => {
            //const round = cr.round
            const source = this.category.rounds?.find(r => r.roundIndex === cr.round.roundIndex)
            if (source) {
              /*const results = clone(cr.round.results)
              if (source.results) {
                forOwn(source.results, (val, key) => {
                  results[key] = {
                    result: val
                  }
                })
              }*/

              merge(cr.round, source)

              // cr.round.results = results
              /*if (cr.round.formatId && this.discipline.exerciseTiming === 'exercise') {
                cr.round.exerciseTiming = map(cr.round.exerciseTypes, exerciseType => {
                  const time = find(source.exerciseTiming, time => time.type === exerciseType.id)
                  return time ? time : {type: exerciseType.id, time: undefined}
                })
              }*/
            }
          })
        }
      },
      getIncludeCategories: function() {
        return filter(this.categories, item => item.id !== this.id);
      },
      submit: function () {
        this.$validator.validateAll().then(() => {
          if (!this.errors.any()) {
            let rounds = map(this.categoryRounds, item => {
              const round = item.round

              return {
                categoryId: round.categoryId,
                formatId: round.formatId,
                id: round.id,
                roundIndex: round.roundIndex,
                subDivisions: round.subDivisions,
                singleAwardsType: round.singleAwardsType,
                singleAwards: round.singleAwards,
                eventAwardsType: round.eventAwardsType,
                eventAwards: round.eventAwards,
                teamCount: round.teamCount,
                teamMethod: round.teamMethod,
                teamAwardsType: round.teamAwardsType,
                teamAwards: round.teamAwards,
                tieBreakId: round.tieBreakId,
                results: round.results,
                selectionCriteria: round.selectionCriteria,
                config: round.config,
                exerciseTime: round.exerciseTime,
                exerciseTiming: round.exerciseTiming,
                requireAllEvents: round.requireAllEvents,
                trackSelection: round.trackSelection,
              }
            })

            console.log(rounds)
            const data = {
              id: this.id,
              name: this.name,
              accessId: this.externalId,
              competitionId: this.competitionId,
              description: this.description,
              index: this.index,
              minAge: this.minAge,
              maxAge: this.maxAge,
              scorePublication: this.scorePublication,
              rankingPublication: this.rankingPublication,
              participantType: this.participantType,
              gender: this.gender,
              groupSize: this.groupSize,
              rounds,
              includeCategories: map(this.includeCategories, item => item.id),
              warmup: this.warmup,
            }

            this.$emit('submit', data)
          } else {
            this.$notify.warning('Validation errors')
          }
        })
      },
      remove: function () {
        this.$modal.show({
          title: this.$t('delete.confirm.title'),
          message: this.$t('delete.confirm.text', {item: this.name}),
          onConfirm: () => {
            this.$emit('remove', this.category);
          }
        });
      },
      getFormatDescription: function(formatId) {
        const format = find(this.formats, item => item.id === formatId);
        return format ? format.description : this.$t('category.skipRound.description');
      },
      getTieBreakDescription: function(tieBreakId) {
        if (tieBreakId) {
          return find(this.tieBreaks, function (item) {
            return item.id === tieBreakId;
          }).description;
        }

        return 'Skip tie-break';
      },
    }
  };
</script>
